'use client'

import * as React from 'react'

import { cn } from '../cn'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog'
import { buttonVariants } from './button'

type DestructiveConfirmIntent = 'delete' | 'archive'

export interface DestructiveConfirmOptions {
  title: string
  /** Optional body text under the title. */
  body?: string
  /** Confirm button label. Defaults to "Delete" or "Archive" based on intent. */
  confirmLabel?: string
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string
  /** Describes the destructive action so defaults and docs stay honest. */
  intent?: DestructiveConfirmIntent
}

export interface ArchiveConfirmOptions {
  /** Record display name, e.g. "April 2026 Sales Tax". */
  entityName: string
  /** Lowercase domain noun used in the body, e.g. "tax report". */
  entityType?: string
  title?: string
  body?: string
  confirmLabel?: string
  cancelLabel?: string
}

type ConfirmFn = (opts: DestructiveConfirmOptions) => Promise<boolean>
type ArchiveConfirmFn = (opts: ArchiveConfirmOptions) => Promise<boolean>

interface PendingConfirm extends DestructiveConfirmOptions {
  resolve: (ok: boolean) => void
}

const DestructiveConfirmContext = React.createContext<ConfirmFn | null>(null)

/**
 * Mounts a single shared destructive-confirm dialog at the root of the tree
 * and exposes async confirm calls via `useDestructiveConfirm()` and
 * `useArchiveConfirm()`.
 *
 * Replaces ad-hoc `window.confirm(...)` calls so every destructive
 * confirmation across every Ordo app uses the same styled dialog — matching
 * theme, readable in both light and dark mode, and consistent copy.
 *
 * For Ordo soft deletes, prefer `useArchiveConfirm()`. It standardizes the
 * user-facing language around archiving instead of deletion.
 *
 * Usage:
 *   const confirmArchive = useArchiveConfirm()
 *   // ...
 *   if (!(await confirmArchive({ entityName: 'April 2026 Sales Tax', entityType: 'tax report' }))) return
 *   await archiveRecord()
 */
export function DestructiveConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null)
  const pendingRef = React.useRef<PendingConfirm | null>(null)

  const confirm = React.useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      const nextPending = { ...opts, resolve }
      pendingRef.current = nextPending
      setPending(nextPending)
    })
  }, [])

  const close = React.useCallback((ok: boolean) => {
    const current = pendingRef.current
    if (!current) {
      return
    }

    pendingRef.current = null
    setPending(null)

    window.setTimeout(() => {
      current.resolve(ok)
    }, 0)
  }, [])

  return (
    <DestructiveConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={Boolean(pending)} onOpenChange={(open) => !open && close(false)}>
        {pending ? (
          <AlertDialogContent className="sm:max-w-md" primaryActionOnEnter>
            <AlertDialogHeader>
              <AlertDialogTitle>{pending.title}</AlertDialogTitle>
              <AlertDialogDescription>{pending.body ?? defaultBodyForIntent(pending.intent)}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => close(false)}>{pending.cancelLabel ?? 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction onClick={() => close(true)} className={cn(buttonVariants({ variant: 'destructive' }))}>
                {pending.confirmLabel ?? defaultConfirmLabelForIntent(pending.intent)}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        ) : null}
      </AlertDialog>
    </DestructiveConfirmContext.Provider>
  )
}

/**
 * Returns an async `confirm(opts)` function that resolves to true when the
 * user clicks the destructive action, false otherwise. Must be used under a
 * `DestructiveConfirmProvider`.
 */
export function useDestructiveConfirm(): ConfirmFn {
  const ctx = React.useContext(DestructiveConfirmContext)
  if (!ctx) {
    throw new Error('useDestructiveConfirm must be used inside a DestructiveConfirmProvider')
  }
  return ctx
}

/**
 * Standard confirmation flow for Ordo soft-delete actions. Use this for
 * records that move to archive instead of being permanently deleted.
 */
export function useArchiveConfirm(): ArchiveConfirmFn {
  const confirm = useDestructiveConfirm()

  return React.useCallback(
    (opts) => {
      const entityType = opts.entityType ?? 'record'
      return confirm({
        title: opts.title ?? `Archive ${opts.entityName}?`,
        body:
          opts.body ?? `This is a soft delete. The ${entityType} will move to the archive and stay available there.`,
        confirmLabel: opts.confirmLabel ?? 'Archive',
        cancelLabel: opts.cancelLabel,
        intent: 'archive',
      })
    },
    [confirm],
  )
}

function defaultConfirmLabelForIntent(intent: DestructiveConfirmIntent = 'delete') {
  return intent === 'archive' ? 'Archive' : 'Delete'
}

function defaultBodyForIntent(intent: DestructiveConfirmIntent = 'delete') {
  return intent === 'archive'
    ? 'This is a soft delete. The record will move to the archive and stay available there.'
    : 'This action cannot be undone.'
}
